import { parseWorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/displayMetadata';
import {
  EMPTY_WORKSPACE_AGENT_PILLS,
  type WorkspaceAgentMessagePill,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { type WorkspaceAgentMessagePillsById } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentMessagePillsById';
import {
  flattenWorkspaceAgentAssetPillItems,
  getWorkspaceAgentAssetSubjectKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { type WorkspaceAgentDisplayRow } from '~/components/v2/components/workspace/workspace-agent-panel/utils/groupWorkspaceAgentMessagesForDisplay';
import { isWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentAssetPill';
import { partitionWorkspaceAgentMessagePills } from '~/components/v2/components/workspace/workspace-agent-panel/utils/partitionWorkspaceAgentMessagePills';

export type WorkspaceAgentMessageRowPills = {
  /** Mirrors `hasBubble` for user rows; always true for agent rows. */
  displayHasBubble: boolean;
  /** False for a pills-only send. */
  hasBubble: boolean;
  hasUncitedPills: boolean;
  /** Every pill on the message (all roles), for inline moniker lookup. */
  pills: readonly WorkspaceAgentMessagePill[];
  /** Whether to render `PrivateMessageUncitedAssetScope` above the bubble (display-only dedupe). */
  showUncitedAssetPreview: boolean;
  /** Pills no moniker cites, previewed above the bubble. Only user messages have any. */
  uncitedPills: readonly WorkspaceAgentMessagePill[];
};

export type WorkspaceAgentMessageRowPillsById = ReadonlyMap<string, WorkspaceAgentMessageRowPills>;

type WorkspaceAgentMessageRowPillsIntrinsic = Pick<
  WorkspaceAgentMessageRowPills,
  'hasBubble' | 'hasUncitedPills' | 'pills' | 'uncitedPills'
>;

type IntrinsicRowEntry = WorkspaceAgentMessageRowPillsIntrinsic & { message: WorkspaceAgentMessage };

type EmittedWorkspaceAgentMessageRowPills = {
  displayHasBubble: boolean;
  pills: readonly WorkspaceAgentMessagePill[];
  rowPills: WorkspaceAgentMessageRowPills;
  showUncitedAssetPreview: boolean;
};

/**
 * Intrinsic fields stay valid across thread-order changes. The emitted object is reused only when
 * those fields and the display flags still match, so a tail send does not hand every older row a
 * new identity (which would remeasure the virtualizer).
 */
export type WorkspaceAgentMessageRowPillsCache = {
  emittedByMessage: WeakMap<WorkspaceAgentMessage, EmittedWorkspaceAgentMessageRowPills>;
  intrinsicByMessage: WeakMap<WorkspaceAgentMessage, WorkspaceAgentMessageRowPillsIntrinsic>;
};

export const createWorkspaceAgentMessageRowPillsCache = (): WorkspaceAgentMessageRowPillsCache => ({
  emittedByMessage: new WeakMap(),
  intrinsicByMessage: new WeakMap(),
});

export const EMPTY_WORKSPACE_AGENT_MESSAGE_ROW_PILLS: WorkspaceAgentMessageRowPills = {
  displayHasBubble: true,
  hasBubble: true,
  hasUncitedPills: false,
  pills: EMPTY_WORKSPACE_AGENT_PILLS,
  showUncitedAssetPreview: false,
  uncitedPills: EMPTY_WORKSPACE_AGENT_PILLS,
};

export type BuildWorkspaceAgentMessageRowPillsParams = {
  cache: WorkspaceAgentMessageRowPillsCache;
  pillsByMessageId: WorkspaceAgentMessagePillsById;
  rows: readonly WorkspaceAgentDisplayRow[];
};

/**
 * Membership when the pills still carry items. A count-only artifact handle has none, so those
 * lists are keyed by id — otherwise every echoed multi-select hashes to the same empty string
 * and the later chips never render.
 */
const getUncitedAssetSubjectFingerprint = (uncitedPills: readonly WorkspaceAgentMessagePill[]): string => {
  const assetPills = uncitedPills.filter(isWorkspaceAgentAssetPill);
  const membership = flattenWorkspaceAgentAssetPillItems(assetPills)
    .map((item) => getWorkspaceAgentAssetSubjectKey(item.assetId, item.versionId))
    .sort((left, right) => left.localeCompare(right))
    .join('|');
  const itemlessListIds = assetPills
    .flatMap((pill) => (pill.type === 'assetList' && (pill.items?.length ?? 0) === 0 ? [`list:${pill.id}`] : []))
    .sort((left, right) => left.localeCompare(right))
    .join('|');

  return [membership, itemlessListIds].filter((part) => part.length > 0).join('|');
};

const getWorkspaceAgentMessageRowDisplayFlags = ({
  hasBubble,
  message,
  showUncitedAssetPreview,
}: {
  hasBubble: boolean;
  message: WorkspaceAgentMessage;
  showUncitedAssetPreview: boolean;
}): Pick<WorkspaceAgentMessageRowPills, 'displayHasBubble' | 'showUncitedAssetPreview'> => {
  if (message.role !== 'user') {
    return {
      displayHasBubble: true,
      showUncitedAssetPreview: false,
    };
  }

  return {
    displayHasBubble: hasBubble,
    showUncitedAssetPreview,
  };
};

const reuseOrEmitWorkspaceAgentMessageRowPills = ({
  cache,
  displayHasBubble,
  intrinsic,
  message,
  showUncitedAssetPreview,
}: {
  cache: WorkspaceAgentMessageRowPillsCache;
  displayHasBubble: boolean;
  intrinsic: WorkspaceAgentMessageRowPillsIntrinsic;
  message: WorkspaceAgentMessage;
  showUncitedAssetPreview: boolean;
}): WorkspaceAgentMessageRowPills => {
  const emitted = cache.emittedByMessage.get(message);

  if (
    emitted &&
    emitted.pills === intrinsic.pills &&
    emitted.displayHasBubble === displayHasBubble &&
    emitted.showUncitedAssetPreview === showUncitedAssetPreview
  ) {
    return emitted.rowPills;
  }

  const rowPills: WorkspaceAgentMessageRowPills = {
    ...intrinsic,
    displayHasBubble,
    showUncitedAssetPreview,
  };

  cache.emittedByMessage.set(message, {
    displayHasBubble,
    pills: intrinsic.pills,
    rowPills,
    showUncitedAssetPreview,
  });

  return rowPills;
};

/** Splits each message row's pills into inline vs previewed-above-the-bubble. Run groups are skipped. */
export const buildWorkspaceAgentMessageRowPills = ({
  cache,
  pillsByMessageId,
  rows,
}: BuildWorkspaceAgentMessageRowPillsParams): WorkspaceAgentMessageRowPillsById => {
  const intrinsicByRowId = new Map<string, IntrinsicRowEntry>();

  for (const row of rows) {
    if (row.type !== 'message') {
      continue;
    }

    const content = row.message.content ?? '';
    // Agent messages need theirs too: `remarkWorkspaceAgentPill` resolves monikers from them.
    const pills = pillsByMessageId.get(row.message.id) ?? EMPTY_WORKSPACE_AGENT_PILLS;
    const cached = cache.intrinsicByMessage.get(row.message);

    if (cached && cached.pills === pills) {
      intrinsicByRowId.set(row.id, { ...cached, message: row.message });

      continue;
    }

    // The agent never previews: everything it cites is in its own prose.
    if (row.message.role !== 'user') {
      const agentRowPills: WorkspaceAgentMessageRowPillsIntrinsic = {
        hasBubble: true,
        hasUncitedPills: false,
        pills,
        uncitedPills: EMPTY_WORKSPACE_AGENT_PILLS,
      };

      cache.intrinsicByMessage.set(row.message, agentRowPills);
      intrinsicByRowId.set(row.id, { ...agentRowPills, message: row.message });

      continue;
    }

    const { uncitedPills } = partitionWorkspaceAgentMessagePills({ content, pills });
    // Uncited scope-sent `board` pills are metadata-only (WORK-518) — only assets preview above.
    const hasUncitedPills = uncitedPills.some(isWorkspaceAgentAssetPill);
    const hasStructuredDisplay = parseWorkspaceAgentStructuredDisplay(row.message.metadata) !== null;

    const rowPillsIntrinsic: WorkspaceAgentMessageRowPillsIntrinsic = {
      // A pills-only send has empty content; only draw the "(no content)" bubble when nothing
      // renders above it. Structured hero sends always get a bubble even when content is empty.
      hasBubble: content.length > 0 || !hasUncitedPills || hasStructuredDisplay,
      hasUncitedPills,
      pills,
      uncitedPills,
    };

    cache.intrinsicByMessage.set(row.message, rowPillsIntrinsic);
    intrinsicByRowId.set(row.id, { ...rowPillsIntrinsic, message: row.message });
  }

  let lastRenderedUncitedFingerprint: string | null = null;
  const byRowId = new Map<string, WorkspaceAgentMessageRowPills>();

  for (const row of rows) {
    if (row.type !== 'message') {
      continue;
    }

    const entry = intrinsicByRowId.get(row.id);

    if (!entry) {
      continue;
    }

    const { message, ...intrinsic } = entry;
    let showUncitedAssetPreview = false;

    if (message.role === 'user' && intrinsic.hasUncitedPills) {
      const fingerprint = getUncitedAssetSubjectFingerprint(intrinsic.uncitedPills);

      showUncitedAssetPreview =
        lastRenderedUncitedFingerprint === null || fingerprint !== lastRenderedUncitedFingerprint;

      if (showUncitedAssetPreview) {
        lastRenderedUncitedFingerprint = fingerprint;
      }
    }

    const { displayHasBubble, showUncitedAssetPreview: resolvedShowUncitedAssetPreview } =
      getWorkspaceAgentMessageRowDisplayFlags({
        hasBubble: intrinsic.hasBubble,
        message,
        showUncitedAssetPreview,
      });

    byRowId.set(
      row.id,
      reuseOrEmitWorkspaceAgentMessageRowPills({
        cache,
        displayHasBubble,
        intrinsic,
        message,
        showUncitedAssetPreview: resolvedShowUncitedAssetPreview,
      }),
    );
  }

  return byRowId;
};
